import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edge, SafeAreaView } from 'react-native-safe-area-context';
import { Button, Modal, ModalDialogue, theme } from '@sg/garnish';

import { useGlobalAppState } from '@order/GlobalAppState';
import { useLiveActivity } from '@order/live-activities';
import { useTelemetry } from '@order/Telemetry';

import { useCancelOrderV2 } from './hooks';
import { orderCancellationMessages } from './order-cancellation-messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Order cancellation modal with built-in mutation handler
 *
 * Should be used together with the `useOrderCancellationModal` hook.
 *
 * @example
 *
 * import { OrderCancellationModal, useOrderCancellationModal } from '@order/components';
 *
 * const {
 *   isOrderCancellationModalVisible,
 *   showOrderCancellationModal,
 *   hideOrderCancellationModal,
 * } = useOrderCancellationModal();
 *
 * return (
 *   <>
 *    <Button onPress={showOrderCancellationModal}>Cancel Order</Button>
 *    <OrderCancellationModal isVisible={isOrderCancellationModalVisible} onRequestClose={hideOrderCancellationModal} ... />
 *   </>
 * )
 */
export const OrderCancellationModal = (props: OrderCancellationModalProps) => {
  const {
    isVisible,
    orderId,
    restaurantId,
    onSuccess,
    onAsyncCancelation,
    onRequestClose,
  } = props;

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  const { dynamicFeatureFlags } = useGlobalAppState();
  const { isOneSignalLiveActivitiesEnabled, isBrazeLiveActivitiesEnabled } =
    dynamicFeatureFlags;

  const isLiveActivitiesEnabled =
    isOneSignalLiveActivitiesEnabled || isBrazeLiveActivitiesEnabled;

  const { cancelLiveActivity } = useLiveActivity();

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Some orders can be canceled asynchronously, which takes approximately
   * ~5 seconds.
   *
   * To handle these cases, we call the special `onAsyncCancelation` function
   * after 10 seconds to ensure that the pending cancelation is completed
   * or failed.
   */
  const handleAsyncCancelation = useCallback(() => {
    onAsyncCancelation?.(orderId);
    onRequestClose?.();

    if (isLiveActivitiesEnabled) {
      cancelLiveActivity?.(orderId);
    }
  }, [
    cancelLiveActivity,
    isLiveActivitiesEnabled,
    onAsyncCancelation,
    onRequestClose,
    orderId,
  ]);

  const handleSuccess = useCallback(() => {
    onSuccess?.(orderId);
    onRequestClose?.();

    if (isLiveActivitiesEnabled) {
      cancelLiveActivity?.(orderId);
    }
  }, [
    cancelLiveActivity,
    isLiveActivitiesEnabled,
    onRequestClose,
    onSuccess,
    orderId,
  ]);

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { isCancellingOrder, cancelOrder } = useCancelOrderV2({
    orderId,
    restaurantId,
    onSuccess: handleSuccess,
    onAsyncCancelation: handleAsyncCancelation,
    onError: onRequestClose,
  });

  // ─────────────────────────────────────────────────────────────────────

  const confirmOrderCancellation = useCallback(() => {
    track('order_cancel.confirmed');

    void cancelOrder();
  }, [cancelOrder, track]);

  const closeModal = useCallback(() => {
    if (isCancellingOrder) return;

    onRequestClose();
  }, [isCancellingOrder, onRequestClose]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      visible={isVisible}
      size="small"
      headline={formatMessage(orderCancellationMessages.modalHeading)}
      body={formatMessage(orderCancellationMessages.modalBody)}
      accessibilityLabel={formatMessage(
        orderCancellationMessages.modalA11yLabel,
      )}
      onRequestClose={closeModal}
    >
      <Modal.Footer withoutTopBorder>
        <SafeAreaView style={styles.footer} edges={SAFE_AREA_EDGES}>
          <View style={styles.ctaContainer}>
            <Button
              size="large"
              palette="secondary"
              onPress={closeModal}
              disabled={isCancellingOrder}
            >
              <FormattedMessage
                {...orderCancellationMessages.modalGoBackCtaLabel}
              />
            </Button>
          </View>

          <View style={styles.ctaContainer}>
            <Button
              size="large"
              palette="caution"
              onPress={confirmOrderCancellation}
              isLoading={isCancellingOrder}
            >
              <FormattedMessage
                {...orderCancellationMessages.modalCancelOrderCtaLabel}
              />
            </Button>
          </View>
        </SafeAreaView>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edge[] = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  ctaContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderCancellationModalProps = Readonly<{
  orderId: string;
  restaurantId: string;
  isVisible: boolean;
  onRequestClose: () => void;
  onSuccess?: (orderId: string) => void;

  // NOTE: Some orders (like IK orders) can be canceled only asynchronously.
  onAsyncCancelation?: (orderId: string) => void;
}>;
